import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import PageConsensusBanner from '../components/PageConsensusBanner';
import FormulaExplainer from '../components/FormulaExplainer';
import ConvergencePanel from '../components/ConvergencePanel';
import BacktestPanel from '../components/BacktestPanel';
import BlindSpotsBanner, { buildTopPicks } from '../components/BlindSpotsBanner';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import {
  Star,
  ArrowLeft,
  CircleNotch,
  DownloadSimple,
  CaretUp,
  CaretDown,
  ArrowsOutLineHorizontal,
  Target,
  ArrowsClockwise,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { syncConstraintsToDB } from '../components/MyPrediction';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
import W3FamilyFinalVerdict from '../components/W3FamilyFinalVerdict';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const DEFAULT_COLUMN_WIDTHS = {
  difference: 60,
  overall: 70,
  overall_pct: 75,
  month_total: 85,
  month_pct: 75,
  diff_pct: 70,
  month_yr1: 70,
  month_yr2: 70,
  month_yr3: 70,
  cumulative_3yr: 95,
  prev_months_current_year: 95,
  month_current_year: 75,
};

const ResizableHeader = ({ children, width, onResize, columnKey, onClick, className = '' }) => {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    const diff = e.clientX - startXRef.current;
    const newWidth = Math.max(40, startWidthRef.current + diff);
    onResize(columnKey, newWidth);
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  return (
    <th
      className={`relative ${className}`}
      style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
      onClick={onClick}
    >
      <div className="flex items-center justify-center gap-1 pr-2 font-bold">
        {children}
      </div>
      <div
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center"
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
      </div>
    </th>
  );
};

const BallTable = ({ ballData, columnWidths, onColumnResize, sortConfig, onSort, meta }) => {
  const matrix = ballData?.matrix || [];
  const mon = meta.current_month_abbr || 'Mon';
  const yr1 = meta.yr1;
  const yr2 = meta.yr2;
  const yr3 = meta.yr3;
  const cy = meta.current_year;
  const prevLabel = meta.prev_months_label || 'Prev';

  // Total 2026 draws counted for this ball = sum of (prev_months_current_year + month_current_year)
  // across all rows. Since each draw contributes exactly one Diff row, this equals the number of
  // 2026 draws in the dataset for this ball.
  const total2026Draws = matrix.reduce(
    (s, r) => s + (r.prev_months_current_year || 0) + (r.month_current_year || 0),
    0
  );
  const cyShort = String(cy).slice(2);

  const sortedData = [...matrix].sort((a, b) => {
    const getVal = (r) => {
      const total2026 = (r.prev_months_current_year || 0) + (r.month_current_year || 0);
      if (sortConfig.key === 'total_2026') return total2026;
      if (sortConfig.key === 'total_2026_pct') {
        return total2026Draws > 0 ? (total2026 / total2026Draws) * 100 : 0;
      }
      if (sortConfig.key === 'diff_pct_2026') {
        const pct = total2026Draws > 0 ? (total2026 / total2026Draws) * 100 : 0;
        return pct - (r.overall_pct || 0);
      }
      return r[sortConfig.key] ?? 0;
    };
    const aVal = getVal(a);
    const bVal = getVal(b);
    return sortConfig.direction === 'desc' ? bVal - aVal : aVal - bVal;
  });
  const topPredicted = new Set([...matrix].filter(r => !r.is_impossible).sort((a, b) => (b.probability_score || 0) - (a.probability_score || 0)).slice(0, 5).map(r => r.difference));

  const SortIcon = ({ sortKey }) => {
    if (sortConfig.key !== sortKey) return null;
    return sortConfig.direction === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
  };

  const getDiffPctColor = (pct) => {
    if (pct > 0) return 'text-green-600';
    if (pct < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const headerCls = "px-2 py-2 text-xs uppercase tracking-wider text-gray-700 cursor-pointer hover:bg-gray-200 text-center";

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" style={{ tableLayout: 'fixed', minWidth: '900px' }}>
        <thead className="bg-gray-100">
          <tr>
            <ResizableHeader width={columnWidths.difference} onResize={onColumnResize} columnKey="difference" onClick={() => onSort('difference')} className={headerCls}>
              Diff <SortIcon sortKey="difference" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.overall} onResize={onColumnResize} columnKey="overall" onClick={() => onSort('overall')} className={headerCls}>
              Overall <SortIcon sortKey="overall" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.overall_pct} onResize={onColumnResize} columnKey="overall_pct" onClick={() => onSort('overall_pct')} className={`${headerCls} bg-gray-200`}>
              Overall% <SortIcon sortKey="overall_pct" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_total} onResize={onColumnResize} columnKey="month_total" onClick={() => onSort('month_total')} className={`${headerCls} bg-blue-100`}>
              {mon} <SortIcon sortKey="month_total" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_pct} onResize={onColumnResize} columnKey="month_pct" onClick={() => onSort('month_pct')} className={`${headerCls} bg-blue-100`}>
              {mon}% <SortIcon sortKey="month_pct" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.diff_pct} onResize={onColumnResize} columnKey="diff_pct" onClick={() => onSort('diff_pct')} className={`${headerCls} bg-purple-100`}>
              Diff% <SortIcon sortKey="diff_pct" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_yr1} onResize={onColumnResize} columnKey="month_yr1" onClick={() => onSort('month_yr1')} className={`${headerCls} bg-amber-100`}>
              {mon}{String(yr1).slice(2)} <SortIcon sortKey="month_yr1" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_yr2} onResize={onColumnResize} columnKey="month_yr2" onClick={() => onSort('month_yr2')} className={`${headerCls} bg-amber-100`}>
              {mon}{String(yr2).slice(2)} <SortIcon sortKey="month_yr2" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_yr3} onResize={onColumnResize} columnKey="month_yr3" onClick={() => onSort('month_yr3')} className={`${headerCls} bg-amber-100`}>
              {mon}{String(yr3).slice(2)} <SortIcon sortKey="month_yr3" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.cumulative_3yr} onResize={onColumnResize} columnKey="cumulative_3yr" onClick={() => onSort('cumulative_3yr')} className={`${headerCls} bg-purple-100`}>
              {mon}{String(yr1).slice(2)}-{String(yr3).slice(2)} <SortIcon sortKey="cumulative_3yr" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.prev_months_current_year} onResize={onColumnResize} columnKey="prev_months_current_year" onClick={() => onSort('prev_months_current_year')} className={`${headerCls} bg-orange-100`}>
              {prevLabel}{String(cy).slice(2)} <SortIcon sortKey="prev_months_current_year" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.month_current_year} onResize={onColumnResize} columnKey="month_current_year" onClick={() => onSort('month_current_year')} className={`${headerCls} bg-green-100`}>
              {mon}{String(cy).slice(2)} <SortIcon sortKey="month_current_year" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.total_2026 || 80} onResize={onColumnResize} columnKey="total_2026" onClick={() => onSort('total_2026')} className={`${headerCls} bg-teal-100`}>
              {cyShort} Tot <SortIcon sortKey="total_2026" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.total_2026_pct || 80} onResize={onColumnResize} columnKey="total_2026_pct" onClick={() => onSort('total_2026_pct')} className={`${headerCls} bg-teal-100`}>
              {cyShort}% <SortIcon sortKey="total_2026_pct" />
            </ResizableHeader>
            <ResizableHeader width={columnWidths.diff_pct_2026 || 100} onResize={onColumnResize} columnKey="diff_pct_2026" onClick={() => onSort('diff_pct_2026')} className={`${headerCls} bg-pink-100`}>
              {cyShort} Diff% <SortIcon sortKey="diff_pct_2026" />
            </ResizableHeader>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, idx) => (
            <tr key={row.difference} className={row.is_impossible ? 'bg-red-50 opacity-40' : topPredicted.has(row.difference) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={row.is_impossible ? {borderLeft: '4px solid #ef4444'} : topPredicted.has(row.difference) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}}>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono font-bold text-center">
                <span className={row.is_impossible ? 'line-through text-red-400' : ''}>{row.difference > 0 ? '+' : ''}{row.difference}</span>
                {row.next_value && <span className={`block text-[9px] font-normal ${row.is_impossible ? 'text-red-400' : 'text-gray-400'}`}>={row.next_value}</span>}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">
                {row.overall || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-gray-50">
                {row.overall_pct?.toFixed(1) || '-'}%
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/50">
                {row.month_total || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/50">
                {row.month_pct?.toFixed(1) || '-'}%
              </td>
              <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-purple-50/50 ${getDiffPctColor(row.diff_pct)}`}>
                {row.diff_pct > 0 ? '+' : ''}{row.diff_pct?.toFixed(1) || '-'}%
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/30">
                {row.month_yr1 || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/30">
                {row.month_yr2 || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/30">
                {row.month_yr3 || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-purple-50/30">
                {row.cumulative_3yr || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-orange-50/30">
                {row.prev_months_current_year || '-'}
              </td>
              <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-green-50/50">
                {row.month_current_year || '-'}
              </td>
              {(() => {
                const total2026 = (row.prev_months_current_year || 0) + (row.month_current_year || 0);
                const pct2026 = total2026Draws > 0 ? (total2026 / total2026Draws) * 100 : 0;
                const diffVsOverall = pct2026 - (row.overall_pct || 0);
                const hasData = total2026 > 0 || (row.overall || 0) > 0;
                return (
                  <>
                    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-teal-50/50 text-teal-800">
                      {total2026 || '-'}
                    </td>
                    <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-teal-50/40 text-teal-700">
                      {total2026 > 0 ? `${pct2026.toFixed(1)}%` : '-'}
                    </td>
                    <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-pink-50/40 ${getDiffPctColor(diffVsOverall)}`}>
                      {hasData ? `${diffVsOverall > 0 ? '+' : ''}${diffVsOverall.toFixed(1)}%` : '-'}
                    </td>
                  </>
                );
              })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Window3_1PatternAX() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [baData, setBaData] = useState(null);
  const [w2Data, setW2Data] = useState(null);
  const [coverageData, setCoverageData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'overall', direction: 'desc' });
  const [w2SortConfig, setW2SortConfig] = useState({ key: 'overall_times', direction: 'desc' });
  const [mainTab, setMainTab] = useState('convergence');
  const [mySignPattern, setMySignPattern] = useState('');
  const [signApplied, setSignApplied] = useState(false);
  const [signSet, setSignSet] = useState(1);
  const [dbSets, setDbSets] = useState({});

  const [lastDrawNum, setLastDrawNum] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollRef = useRef(null);

  const handleSignSetChange = (s) => {
    setSignSet(s);
    const saved = dbSets[`C${s}`]?.sign_pattern || '';
    setMySignPattern(saved);
    setSignApplied(!!saved);
  };

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('window3_column_widths_v3');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMN_WIDTHS;
  });

  useEffect(() => {
    localStorage.setItem('window3_column_widths_v3', JSON.stringify(columnWidths));
  }, [columnWidths]);

  const handleColumnResize = (columnKey, newWidth) => {
    setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
  };

  const resetColumnWidths = () => {
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    toast.success('Column widths reset');
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setIsRefreshing(true);
    try {
      const [res, baRes, w2Res, covRes] = await Promise.all([
        axios.get(`${API}/analysis/per-ball-difference`),
        axios.get(`${API}/analysis/diff-before-after`),
        axios.get(`${API}/analysis/difference-matrix`),
        axios.get(`${API}/analysis/diff-range-coverage`),
      ]);
      setData(res.data);
      setBaData(baRes.data);
      setW2Data(w2Res.data);
      setCoverageData(covRes.data);
      setLastUpdated(new Date());
      // Track latest draw number from response
      const latestDraw = res.data?.last_draw_number || res.data?.total_draws;
      if (latestDraw) setLastDrawNum(latestDraw);
    } catch (error) {
      if (!silent) toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load constraint sets from DB
  useEffect(() => {
    (async () => {
      try {
        const csRes = await axios.get(`${API}/analysis/constraint-sets`);
        const cs = csRes.data || {};
        setDbSets(cs);
        const c1Sign = cs.C1?.sign_pattern || '';
        setMySignPattern(c1Sign);
        setSignApplied(!!c1Sign);
      } catch {}
    })();
  }, []);

  // Poll for new draws every 60 seconds
  useEffect(() => {
    const checkForNewDraw = async () => {
      try {
        const res = await axios.get(`${API}/draws/stats`);
        const latestDraw = res.data?.latest_draw;
        if (latestDraw && lastDrawNum && latestDraw > lastDrawNum) {
          toast.info('New draw detected! Refreshing analysis...', { duration: 3000 });
          fetchData(true);
        }
      } catch {}
    };
    pollRef.current = setInterval(checkForNewDraw, 60000);
    return () => clearInterval(pollRef.current);
  }, [lastDrawNum, fetchData]);

  // Refresh on page focus
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && lastDrawNum) {
        axios.get(`${API}/draws/stats`).then(res => {
          const latestDraw = res.data?.latest_draw;
          if (latestDraw && latestDraw > lastDrawNum) {
            toast.info('New draw detected! Refreshing analysis...', { duration: 3000 });
            fetchData(true);
          }
        }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [lastDrawNum, fetchData]);

  // Instant refresh when a draw is added/updated/deleted elsewhere in the app
  useEffect(() => {
    const handleDrawsUpdated = () => {
      toast.info('Draw data changed — refreshing Window 3.1...', { duration: 2500 });
      fetchData(true);
    };
    window.addEventListener('euromillions:draws-updated', handleDrawsUpdated);
    return () => window.removeEventListener('euromillions:draws-updated', handleDrawsUpdated);
  }, [fetchData]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/analysis/per-ball-difference/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `per_ball_difference_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  const mon = data?.current_month_abbr || 'Mon';
  const monFull = data?.current_month_name || 'Month';
  const cy = data?.current_year || '';
  const yr1 = data?.yr1 || '';
  const yr3 = data?.yr3 || '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />

      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft size={18} />
              </Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center">
                <Star size={24} weight="fill" className="text-amber-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Window 3.1 (A-X)</h1>
                <p className="text-xs text-gray-500">Pattern Predictor (Sign Pattern)</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {lastUpdated && (
                <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="auto-update-indicator">
                  {isRefreshing && <CircleNotch size={14} className="animate-spin text-blue-500" />}
                  <span>Draw #{data?.last_draw_number || '—'}</span>
                  <span className="text-gray-300">|</span>
                  <span>{lastUpdated.toLocaleTimeString()}</span>
                  <button
                    onClick={() => fetchData(true)}
                    disabled={isRefreshing}
                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                    title="Refresh now"
                    data-testid="refresh-btn"
                  >
                    <ArrowsClockwise size={14} className={isRefreshing ? 'animate-spin text-blue-500' : 'text-gray-400 hover:text-gray-600'} />
                  </button>
                </div>
              )}
              <Button onClick={resetColumnWidths} variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700" title="Reset column widths">
                <ArrowsOutLineHorizontal size={18} />
              </Button>
              <Button onClick={handleExport} disabled={exportLoading} className="btn-secondary flex items-center gap-2" data-testid="export-btn">
                {exportLoading ? <CircleNotch size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
                Export Excel
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <W3FamilyFinalVerdict activeWindow="W3.1" />
        <WindowNarrative windowId="W3_1" {...NARRATIVES.W3_1} className="mb-5" />
        <PageConsensusBanner className="mb-4" />
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={32} className="animate-spin text-[#002FA7]" />
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Total Draws</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.total_draws}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Analysis Type</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>Per Ball</p>
                <p className="text-xs text-gray-400">Ball 1 to Ball 5</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 bg-blue-50 border-blue-200">
                <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Current Month</p>
                <p className="text-2xl font-bold text-blue-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  {monFull} {cy}
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Latest Draw</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>#{data.last_draw_number || '—'}</p>
                <p className="text-xs text-gray-400">{data.last_draw_date || ''}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Auto-Refresh</p>
                <p className="text-xs text-gray-600 mt-1">
                  Checks every 60s for new draws. Also refreshes on page focus.
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  Last: {lastUpdated ? lastUpdated.toLocaleTimeString() : '—'}
                </p>
              </div>
            </div>

            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
              <ArrowsOutLineHorizontal size={14} />
              <span>Drag column borders to resize | <strong>Diff%</strong> = {mon}% - Overall% | Auto-adapts to current month</span>
            </div>

            {/* My Sign Pattern Prediction */}
            <div className={`rounded-lg p-5 text-white transition-all mb-4 ${signApplied ? 'bg-gradient-to-r from-emerald-600 to-green-700 ring-2 ring-emerald-400' : 'bg-gradient-to-r from-gray-600 to-gray-700'}`} data-testid="my-prediction-sign">
              {/* C1/C2/C3 selector */}
              <div className="flex items-center gap-1.5 mb-3">
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => handleSignSetChange(s)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${signSet === s ? (s===1?'bg-blue-600':s===2?'bg-purple-600':'bg-teal-600')+' text-white ring-2 ring-white/40 shadow-lg' : !!dbSets[`C${s}`]?.sign_pattern ? 'bg-white/20 text-white/90' : 'bg-white/10 text-white/50 hover:bg-white/15'}`} data-testid={`sign-set-${s}`}>C{s}</button>
                ))}
                <span className="text-[10px] text-white/40 ml-2">5 independent sets</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg">My Diff Sign Prediction</h3>
                  <p className="text-xs text-white/70">Set your predicted sign pattern for B1-B5 (e.g., ▲▼▲▼▲) — applies across prediction windows</p>
                </div>
                {signApplied ? (
                  <span className="text-xs bg-emerald-400/30 px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 ring-1 ring-emerald-300">
                    <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span> APPLIED
                  </span>
                ) : (
                  <span className="text-xs bg-white/10 px-3 py-1.5 rounded-full font-bold text-white/60">NOT SET</span>
                )}
              </div>

              {/* C1-C5 overview strip */}
              <div className="flex flex-wrap gap-2 mb-3 bg-white/10 rounded-lg px-4 py-2.5">
                {[1,2,3,4,5].map(s => {
                  const val = dbSets[`C${s}`]?.sign_pattern;
                  return (
                    <div key={s} className={`flex items-center gap-1.5 text-xs ${signSet === s ? 'ring-1 ring-white/40 rounded px-1.5 py-0.5 bg-white/10' : ''}`}>
                      <span className={`font-bold ${val ? 'text-emerald-300' : 'text-white/30'}`}>C{s}:</span>
                      {val ? (
                        <span className="font-mono font-bold text-white/90">
                          {val.split(',').map(v => v === '+' ? '▲' : v === '-' ? '▼' : '—').join('')}
                        </span>
                      ) : (
                        <span className="text-white/30 italic">not set</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {['B1','B2','B3','B4','B5'].map((b, i) => {
                  const signs = mySignPattern ? mySignPattern.split(',') : [];
                  const val = signs[i] || '';
                  return (
                    <div key={b} className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold opacity-70">{b}</span>
                      <select value={val} onChange={e => {
                        const newSigns = mySignPattern ? mySignPattern.split(',') : ['','','','',''];
                        while (newSigns.length < 5) newSigns.push('');
                        newSigns[i] = e.target.value;
                        setMySignPattern(newSigns.join(','));
                        setSignApplied(false);
                      }} className="w-16 px-1 py-1.5 rounded text-gray-900 font-mono font-bold text-center text-xs" data-testid={`sign-select-${i}`}>
                        <option value="">-</option>
                        <option value="+">▲ (+)</option>
                        <option value="-">▼ (-)</option>
                      </select>
                    </div>
                  );
                })}
                {/* Quick pick from top prediction */}
                {data?.sign_pattern_analysis?.prediction?.after_last_pattern?.[0]?.pattern && (
                  <button onClick={() => {
                    const pat = data.sign_pattern_analysis.prediction.after_last_pattern[0].pattern;
                    setMySignPattern(pat.split('').join(','));
                    setSignApplied(false);
                  }} className="px-2 py-1 rounded text-[10px] font-mono font-bold bg-white/15 hover:bg-white/25 transition-colors ml-1">
                    Top: {data.sign_pattern_analysis.prediction.after_last_pattern[0].pattern}
                  </button>
                )}
                <Button onClick={() => {
                  setSignApplied(true); setDbSets(prev => ({ ...prev, [`C${signSet}`]: { ...prev[`C${signSet}`], sign_pattern: mySignPattern } })); syncConstraintsToDB(signSet, { sign_pattern: mySignPattern }); toast.success(`Sign pattern C${signSet} applied: ${mySignPattern}`); }}
                  className={`font-bold transition-all ${signApplied ? 'bg-emerald-500 text-white hover:bg-emerald-400 ring-2 ring-emerald-300' : 'bg-amber-400 text-amber-900 hover:bg-amber-300'}`} data-testid="save-sign-btn">
                  {signApplied ? 'Applied' : 'Save Constraint'}
                </Button>
                {signApplied && (
                  <Button onClick={() => { setSignApplied(false); setMySignPattern(''); setDbSets(prev => ({ ...prev, [`C${signSet}`]: { ...prev[`C${signSet}`], sign_pattern: null } })); syncConstraintsToDB(signSet, { sign_pattern: null }); toast.info(`Sign pattern C${signSet} cleared`); }}
                    variant="ghost" className="text-white/70 hover:text-white text-xs">Clear</Button>
                )}
              </div>
            </div>

            {/* Formula Explainer (collapsible) */}
            <FormulaExplainer context="sign-pattern" />

            {/* Main Tab Switcher — W3.1: Convergence + Formula 1 + Formula 2 */}
            <div className="flex gap-2 bg-white border border-gray-200 rounded-lg p-1.5 mb-4" data-testid="w3-main-tabs">
              <button onClick={() => setMainTab('convergence')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'convergence' ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-convergence">
                🎯 Convergence
              </button>
              <button onClick={() => setMainTab('signs')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'signs' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-signs">
                Formula 1 (Long-term)
              </button>
              <button onClick={() => setMainTab('prediction')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'prediction' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-prediction">
                Formula 2 (Next-draw)
              </button>
            </div>

            {/* === CONVERGENCE TAB === */}
            {mainTab === 'convergence' && data?.sign_pattern_analysis && (
              <div className="space-y-5">
                <BacktestPanel endpoint="sign-pattern" />
                <BlindSpotsBanner
                  riskFlags={data.sign_pattern_analysis.risk_flags}
                  topPicks={buildTopPicks(data.sign_pattern_analysis.convergence)}
                  itemLabel="patterns"
                  renderItem={(pat) => (
                    <span className="font-mono font-bold tracking-wider">
                      {pat.split('').map((s, i) => (
                        <span key={`bs-${pat}-${i}`} className={s === '+' ? 'text-emerald-600' : s === '-' ? 'text-red-500' : 'text-gray-400'}>
                          {s === '+' ? '\u25B2' : s === '-' ? '\u25BC' : '='}
                        </span>
                      ))}
                    </span>
                  )}
                />
                <ConvergencePanel
                  convergence={data.sign_pattern_analysis.convergence}
                  itemLabel="Pattern"
                  renderItem={(pat) => (
                    <span className="font-mono font-bold tracking-wider text-base">
                      {pat.split('').map((s, i) => (
                        <span key={i} className={s === '+' ? 'text-emerald-600' : s === '-' ? 'text-red-500' : 'text-gray-400'}>
                          {s === '+' ? '\u25B2' : s === '-' ? '\u25BC' : '='}
                        </span>
                      ))}
                    </span>
                  )}
                  f1Lookup={(pat) => {
                    const m = (data.sign_pattern_analysis.top_patterns || []).find(p => p.pattern === pat);
                    return m ? { score: m.pred_score, score_v_adj: m.pred_score_volatility_adj } : null;
                  }}
                  f2Lookup={(pat) => {
                    const m = (data.sign_pattern_analysis.prediction?.after_last_pattern || []).find(p => p.pattern === pat);
                    return m ? { score: m.combined_score } : null;
                  }}
                />
              </div>
            )}

            {/* Tab 0: Final Prediction */}
            {mainTab === 'prediction' && data?.sign_pattern_analysis && (() => {
              const spa = data.sign_pattern_analysis;
              const patPred = (spa.prediction?.after_last_pattern || []).slice(0, 5);
              const topPattern = patPred[0]?.pattern || '';
              // Step 1: Build candidates per ball — UNFILTERED by direction, ranked by score
              const ballCandidates = ['ball_1','ball_2','ball_3','ball_4','ball_5'].map((bk, bi) => {
                const matrix = data.balls?.[bk]?.matrix || [];
                const allValid = [...matrix].filter(r =>
                  r.probability_score && r.probability_score > 0 && !r.is_impossible
                ).sort((a, b) => b.probability_score - a.probability_score);
                return { key: bk, label: `Ball ${bi + 1}`, last: data.last_values?.[bk], candidates: allValid };
              });
              // Step 2: Sequential pick enforcing Ball1 < Ball2 < Ball3 < Ball4 < Ball5
              let floorValue = 0;
              const ballPicks = ballCandidates.map((bc) => {
                const valid = bc.candidates.filter(r => r.next_value > floorValue);
                const top5 = valid.slice(0, 5);
                const top5Diffs = new Set(top5.map(r => r.difference));
                const overdueDue = valid
                  .filter(r => r.is_overdue && !top5Diffs.has(r.difference))
                  .sort((a, b) => (b.overdue_ratio || 0) - (a.overdue_ratio || 0))
                  .slice(0, 3);
                if (top5.length > 0) floorValue = top5[0].next_value;
                return { ...bc, top5, overdueDue };
              });

              return (
                <div className="space-y-4">
                  {/* Combined Prediction Header */}
                  <div className="bg-gradient-to-r from-[#002FA7] to-indigo-800 rounded-xl p-6 text-white" data-testid="final-prediction-header">
                    <h2 className="text-2xl font-bold mb-1">Final Pattern Prediction — Window 3.1</h2>
                    <p className="text-sm text-blue-200">
                      Sign Pattern Prediction (transition + gap + year diff) combined with top-ball numbers projection.
                      Last pattern: <span className="font-mono font-bold tracking-wider">{spa.last_pattern?.split('').map(s => s === '+' ? '\u25B2' : s === '-' ? '\u25BC' : '=').join('')}</span>
                    </p>
                  </div>

                  {/* Top Pattern Predictions */}
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-3">
                      <h3 className="font-bold">Block 1 — Predicted Next Pattern</h3>
                      <p className="text-xs text-indigo-200 mt-0.5">Score = 30% Transition + 30% Gap Overdue + 20% Year Diff + 20% Base Freq</p>
                    </div>
                    <div className="p-4 space-y-2">
                      {patPred.map((p, idx) => (
                        <div key={p.pattern} className={`flex items-center gap-4 p-3 rounded-lg ${idx === 0 ? 'bg-indigo-50 border-2 border-indigo-300' : 'bg-gray-50'}`}>
                          <div className="w-8 text-center font-bold text-gray-400">{idx + 1}</div>
                          <div className="font-mono font-bold text-xl tracking-wider flex gap-1">
                            {p.pattern.split('').map((s, si) => (
                              <span key={si} className={s === '+' ? 'text-emerald-600' : s === '-' ? 'text-red-500' : 'text-gray-400'}>
                                {s === '+' ? '\u25B2' : s === '-' ? '\u25BC' : '='}
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-4 text-xs">
                            <span className={`font-mono ${p.count > 0 ? 'text-indigo-600 font-bold' : 'text-gray-400'}`}>Trans: {p.count > 0 ? `${p.count}x` : '-'}</span>
                            <span className={`font-mono font-bold ${p.gap_ratio >= 1.5 ? 'text-red-600' : p.gap_ratio >= 1 ? 'text-orange-600' : 'text-gray-500'}`}>Due: {p.gap_ratio}x</span>
                            <span className={`font-mono font-bold ${p.year_diff < -2 ? 'text-red-600' : p.year_diff < 0 ? 'text-orange-500' : p.year_diff > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                              YrDiff: {p.year_diff > 0 ? '+' : ''}{p.year_diff}%
                            </span>
                          </div>
                          <div className="ml-auto">
                            <span className={`font-mono font-bold text-lg ${idx === 0 ? 'text-indigo-700' : 'text-gray-600'}`}>{p.combined_score}</span>
                          </div>
                          {idx === 0 && <span className="text-[10px] bg-indigo-600 text-white font-bold px-2 py-1 rounded-full">TOP</span>}
                        </div>
                      ))}
                    </div>
                  </div>


                  {/* Combined Summary */}
                  <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-5 text-white" data-testid="combined-summary">
                    <h3 className="font-bold text-lg mb-3">Block 2 — Combined Prediction Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white/10 rounded-lg p-4">
                        <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Next Pattern</div>
                        <div className="font-mono font-bold text-2xl tracking-wider flex gap-1 mb-1">
                          {(patPred[0]?.pattern || '').split('').map((s, si) => (
                            <span key={si} className={s === '+' ? 'text-emerald-400' : s === '-' ? 'text-red-400' : 'text-gray-400'}>
                              {s === '+' ? '\u25B2' : s === '-' ? '\u25BC' : '='}
                            </span>
                          ))}
                        </div>
                        <div className="text-xs text-white/60">
                          Score: {patPred[0]?.combined_score} | Due: {patPred[0]?.gap_ratio}x | Yr: {patPred[0]?.year_diff > 0 ? '+' : ''}{patPred[0]?.year_diff}%
                        </div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-4">
                        <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Predicted Numbers</div>
                        <div className="flex gap-2 mb-1">
                          {ballPicks.map((bp, bi) => {
                            const top = bp.top5[0];
                            if (!top) return null;
                            return (
                              <div key={bp.key} className="text-center">
                                <div className="inline-flex w-10 h-10 rounded-full bg-white text-[#002FA7] items-center justify-center font-mono font-bold text-sm">{top.next_value}</div>
                                <div className={`text-[10px] font-mono mt-0.5 ${top.difference > 0 ? 'text-emerald-400' : top.difference < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                                  {top.difference > 0 ? '+' : ''}{top.difference}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-[10px] text-white/40 mt-1">
                          {ballPicks.map(bp => bp.top5[0] ? `B${bp.label.slice(-1)}:${bp.top5[0].next_value}` : '').join(' | ')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}


            {/* Tab 1: Sign Pattern Analysis */}
            {mainTab === 'signs' && data?.sign_pattern_analysis && (() => {
              const spa = data.sign_pattern_analysis;
              const topPats = spa.top_patterns || [];
              const streak = spa.streak || {};
              return (
                <div className="mb-6 space-y-4">
                  {/* Header stats */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-slate-800 to-slate-700">
                      <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'var(--font-heading)' }}>
                        Block 1 — Sign Pattern Analysis
                      </h3>
                      <p className="text-xs text-slate-300 mt-0.5">
                        For each draw, are all 5 ball differences positive, negative, or mixed? &middot; {spa.total_transitions} transitions analyzed
                      </p>
                    </div>

                    <div className="p-4">
                      {/* Key stats row */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                          <p className="text-xs uppercase tracking-wider font-bold text-emerald-600">All Positive (+++++)</p>
                          <p className="text-2xl font-bold text-emerald-700 mt-1 font-mono">{spa.all_positive.count}</p>
                          <p className="text-sm font-bold text-emerald-500">{spa.all_positive.pct}%</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                          <p className="text-xs uppercase tracking-wider font-bold text-red-600">All Negative (-----)</p>
                          <p className="text-2xl font-bold text-red-700 mt-1 font-mono">{spa.all_negative.count}</p>
                          <p className="text-sm font-bold text-red-500">{spa.all_negative.pct}%</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                          <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Last Draw Pattern</p>
                          <p className="text-xl font-bold text-blue-700 mt-1 font-mono tracking-widest">{spa.last_pattern || '-'}</p>
                          <p className="text-xs text-blue-500">
                            {spa.last_pattern && spa.last_pattern.split('').every(c => c === '+') ? 'ALL UP' :
                             spa.last_pattern && spa.last_pattern.split('').every(c => c === '-') ? 'ALL DOWN' : 'MIXED'}
                          </p>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                          <p className="text-xs uppercase tracking-wider font-bold text-amber-600">Current Streak</p>
                          <p className="text-2xl font-bold text-amber-700 mt-1 font-mono">{streak.count || 0}</p>
                          <p className="text-xs text-amber-500">{(streak.type || '').replace(/_/g, ' ')}</p>
                        </div>
                      </div>

                      {/* All Sign Patterns Table — sorted biggest to smallest */}
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">All Sign Patterns — Till 2025 vs 2026 ({topPats.length} unique)</h4>
                        <p className="text-[10px] text-gray-500 mb-3">Score = Freq% × (Current Gap / Expected Gap)<sup>0.5</sup> — same formula as W2 Block 3</p>
                        <div className="overflow-x-auto" data-testid="sign-patterns-table">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-100 sticky top-0">
                              <tr>
                                <th className="px-2 py-2 text-left text-[10px] uppercase">#</th>
                                <th className="px-2 py-2 text-left text-[10px] uppercase">Pattern</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase">Count</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase">Freq %</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase bg-blue-50">2026 Count</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase bg-blue-50">Diff %</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase">Exp Gap</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase">Current Gap</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase">Overdue</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase bg-amber-50 font-bold">Score</th>
                                <th className="px-2 py-2 text-center text-[10px] uppercase">In 2026</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const rows = [...topPats].sort((a, b) => (b.count || 0) - (a.count || 0));
                                const distinctScores = [...new Set(rows.map(r => r.pred_score || 0))].sort((a, b) => b - a);
                                const top2Threshold = distinctScores.length >= 2 ? distinctScores[1] : (distinctScores[0] || 0);
                                return rows.map((p, idx) => {
                                  const isTopScore = (p.pred_score || 0) >= top2Threshold && top2Threshold > 0;
                                  return (
                                <tr key={p.pattern}
                                  className={p.is_all_positive ? 'bg-emerald-50' : p.is_all_negative ? 'bg-red-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                  style={idx === 0 ? { borderLeft: '4px solid #002FA7' } : {}}
                                >
                                  <td className="px-2 py-1.5 border-b font-bold text-gray-500">{idx + 1}</td>
                                  <td className="px-2 py-1.5 border-b">
                                    <div className="font-mono font-bold text-sm tracking-wider flex gap-0.5">
                                      {p.pattern.split('').map((s, si) => (
                                        <span key={si} className={s === '+' ? 'text-emerald-600' : s === '-' ? 'text-red-500' : 'text-gray-400'}>
                                          {s === '+' ? '\u25B2' : s === '-' ? '\u25BC' : '='}
                                        </span>
                                      ))}
                                    </div>
                                    <span className="text-[9px] text-gray-400">{p.positive_count}+/{p.negative_count}-{p.zero_count > 0 ? `/${p.zero_count}=` : ''}</span>
                                  </td>
                                  <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{p.count}</td>
                                  <td className="px-2 py-1.5 border-b font-mono text-center">{p.pct}%</td>
                                  <td className="px-2 py-1.5 border-b font-mono text-center font-bold bg-blue-50/40">{p.y2026_count || 0}</td>
                                  <td className="px-2 py-1.5 border-b text-center">
                                    <span className={`font-mono font-bold text-xs px-2 py-0.5 rounded ${(p.diff || 0) > 0 ? 'text-emerald-700 bg-emerald-50' : (p.diff || 0) < 0 ? 'text-red-600 bg-red-50' : 'text-gray-400'}`}>
                                      {(p.diff || 0) > 0 ? '+' : ''}{p.diff || 0}%
                                    </span>
                                  </td>
                                  <td className="px-2 py-1.5 border-b font-mono text-center text-gray-500">{p.expected_gap}</td>
                                  <td className={`px-2 py-1.5 border-b font-mono text-center font-bold ${(p.overdue_ratio || 0) >= 2 ? 'text-red-600' : (p.overdue_ratio || 0) >= 1 ? 'text-amber-600' : 'text-gray-600'}`}>{p.current_gap}</td>
                                  <td className={`px-2 py-1.5 border-b font-mono text-center font-bold ${(p.overdue_ratio || 0) >= 2 ? 'text-red-600 bg-red-50' : (p.overdue_ratio || 0) >= 1 ? 'text-amber-600 bg-amber-50' : 'text-emerald-600'}`}>{p.overdue_ratio}x</td>
                                  <td className="px-2 py-1.5 border-b text-center" data-testid={`score-cell-${p.pattern}`}>
                                    {isTopScore ? (
                                      <span className="inline-flex w-10 h-10 rounded-full items-center justify-center bg-emerald-500 text-white font-mono font-black text-xs shadow-md ring-2 ring-emerald-200">
                                        {p.pred_score}
                                      </span>
                                    ) : (
                                      <span className="font-mono font-bold text-sm text-[#002FA7]">{p.pred_score}</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 border-b text-center">
                                    {p.y2026_count > 0
                                      ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">{p.y2026_count}x ({p.y2026_pct}%)</span>
                                      : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600">MISSING</span>}
                                  </td>
                                </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>


                </div>
              );
            })()}


            {/* Delta Pattern tab removed per user request */}
          </>
        ) : null}
      </main>
    </div>
  );
}

// ============================================================
// W3 Delta Pattern Match Tab — per-ball increment predictor
// Same framework as W59: Actual + Adaptive + Fallback + Tier consensus
// Covers: Ball 1-5 + LS 1-2 (7 series total)
// ============================================================

function DeltaPatternTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [patternLen, setPatternLen] = useState(5);
  const [tolerance, setTolerance] = useState(2);
  const [pctTolerance, setPctTolerance] = useState(15);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = (silent = false) => {
      if (!silent) setLoading(true);
      axios.get(`${API}/api/analysis/w3-delta-pattern-match?pattern_len=${patternLen}&tolerance=${tolerance}&pct_tolerance=${pctTolerance}`)
        .then(res => setData(res.data))
        .catch(() => setData(null))
        .finally(() => { if (!silent) setLoading(false); });
    };
    load();
    const onEvt = () => load(true);
    const onStorage = (e) => { if (e.key === 'euromillions:last_draw_update') load(true); };
    window.addEventListener('euromillions:draws-updated', onEvt);
    window.addEventListener('storage', onStorage);
    let bc;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('euromillions');
        bc.onmessage = (ev) => { if (ev.data === 'draws-updated') load(true); };
      }
    } catch {}
    return () => {
      window.removeEventListener('euromillions:draws-updated', onEvt);
      window.removeEventListener('storage', onStorage);
      if (bc) bc.close();
    };
  }, [patternLen, tolerance, pctTolerance, API]);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /><span className="ml-3 text-gray-500">Scanning per-ball delta history...</span></div>;
  }
  if (!data || data.error) {
    return <div className="text-center text-gray-500 py-16">No delta pattern data available.</div>;
  }

  const tierColor = (t) => ({
    ELITE: 'bg-purple-600 text-white ring-purple-300',
    STRONG: 'bg-emerald-600 text-white ring-emerald-300',
    NORMAL: 'bg-blue-500 text-white ring-blue-300',
    WIDE: 'bg-orange-500 text-white ring-orange-300',
    UNRELIABLE: 'bg-gray-400 text-white ring-gray-300',
  }[t] || 'bg-gray-400 text-white ring-gray-300');

  const ballKeys = data.ball_keys || [];
  const perBall = data.per_ball || {};

  return (
    <div className="space-y-4" data-testid="delta-pattern-view">
      {/* Controls */}
      <div className="flex items-center gap-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-indigo-900 uppercase">Pattern length:</label>
          <select value={patternLen} onChange={e => setPatternLen(parseInt(e.target.value))} className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1" data-testid="w3-pattern-len">
            {[3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n} draws</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-indigo-900 uppercase">Actual tol:</label>
          <select value={tolerance} onChange={e => setTolerance(parseInt(e.target.value))} className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1" data-testid="w3-tolerance">
            {[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>±{n}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-indigo-900 uppercase">Adaptive ±%:</label>
          <select value={pctTolerance} onChange={e => setPctTolerance(parseInt(e.target.value))} className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1" data-testid="w3-pct-tolerance">
            {[10, 15, 20, 25, 30, 40, 50].map(n => <option key={n} value={n}>±{n}%</option>)}
          </select>
        </div>
        <div className="text-[10px] text-indigo-700 italic ml-auto max-w-md">
          Pattern = last N deltas between consecutive draws per ball. Predicts next ball value = current + predicted delta.
        </div>
      </div>

      {/* Summary grid — all 7 balls' final bet cards */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-slate-800 text-white px-4 py-2.5">
          <h3 className="font-bold text-sm"><span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block 1</span>Predicted Next Ball Values — Quick Glance</h3>
          <p className="text-[11px] text-white/70 mt-0.5">Tier-adjusted consensus prediction per ball. Click any card for full details below.</p>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {ballKeys.map(({ key, label }) => {
            const b = perBall[key];
            if (!b || b.error) return (
              <div key={key} className="bg-gray-100 border border-gray-200 rounded p-2 text-center">
                <div className="text-[11px] font-bold text-gray-600">{label}</div>
                <div className="text-[10px] text-gray-400">No data</div>
              </div>
            );
            const c = b.consensus || {};
            const p = b.predicted_next_ball || {};
            return (
              <div key={key} className={`rounded p-2 border-2 text-center ${
                c.side_label === 'UP' ? 'bg-emerald-50 border-emerald-300' :
                c.side_label === 'DOWN' ? 'bg-rose-50 border-rose-300' :
                c.side_label === 'MIXED' ? 'bg-amber-50 border-amber-300' :
                'bg-gray-50 border-gray-300'
              }`} data-testid={`delta-summary-${key}`}>
                <div className="text-[11px] font-bold text-gray-700">{label}</div>
                <div className="text-[10px] text-gray-500">Now: <b className="text-gray-800 font-mono">{b.current_ball_value}</b></div>
                {p.center !== null && p.center !== undefined ? (
                  <>
                    <div className={`mt-1 text-lg font-black font-mono ${c.side_label === 'UP' ? 'text-emerald-800' : c.side_label === 'DOWN' ? 'text-rose-800' : 'text-amber-800'}`}>{p.center}</div>
                    <div className="text-[9px] text-gray-500 font-mono">{p.low}–{p.high}</div>
                    <div className={`mt-1 inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full ring-2 ${tierColor(c.tier)}`}>
                      {c.tier === 'ELITE' ? '💎' : c.tier === 'STRONG' ? '🟢' : c.tier === 'NORMAL' ? '🔵' : c.tier === 'WIDE' ? '🟠' : '⚫'} {c.tier}
                    </div>
                    <div className="text-[9px] text-gray-400 mt-0.5">{c.effective_confidence_pct}% · n={c.used_matches_total}</div>
                  </>
                ) : (
                  <div className="mt-1 text-[10px] text-gray-400 italic py-3">Unprecedented</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed per-ball consensus + matrix */}
      <div className="space-y-3">
        {ballKeys.map(({ key, label }) => {
          const b = perBall[key];
          if (!b || b.error) return null;
          const c = b.consensus || {};
          const pn = b.predicted_next_ball || {};
          return (
            <div key={key} className="bg-white border border-gray-200 rounded-lg p-3" data-testid={`delta-detail-${key}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-gray-800">{label}</span>
                  <span className="text-[11px] text-gray-500">Current: <b className="font-mono text-gray-800">{b.current_ball_value}</b></span>
                  <span className="text-[11px] text-gray-500">Last 5 deltas:</span>
                  <div className="flex items-center gap-1">
                    {(b.pattern || []).map((v, i) => (
                      <span key={i} className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded ${i === b.pattern.length - 1 ? 'bg-amber-200 text-amber-900 ring-1 ring-amber-400' : 'bg-gray-100 border border-gray-300 text-gray-700'}`}>
                        {v > 0 ? `+${v}` : v}
                      </span>
                    ))}
                  </div>
                </div>
                {c.bet_center !== null && c.bet_center !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded ring-2 ${tierColor(c.tier)}`}>
                      {c.tier_label}
                    </span>
                  </div>
                )}
              </div>

              {/* Consensus bet */}
              {c.bet_center !== null && c.bet_center !== undefined ? (
                <div className={`rounded-lg p-3 border-2 mb-2 ${
                  c.side_label === 'UP' ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-400' :
                  c.side_label === 'DOWN' ? 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-400' :
                  'bg-gray-50 border-gray-300'
                }`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Next Ball Value Prediction</div>
                      <div className={`text-2xl font-black font-mono ${c.side_label === 'UP' ? 'text-emerald-800' : c.side_label === 'DOWN' ? 'text-rose-800' : 'text-gray-800'}`} data-testid={`predicted-ball-${key}`}>
                        ~{pn.center} <span className="text-sm font-normal text-gray-500">(range {pn.low}–{pn.high})</span>
                      </div>
                      <div className="text-[11px] text-gray-600 mt-0.5">
                        = <b>{b.current_ball_value}</b> + delta ~<b>{pn.predicted_delta}</b> · {c.side_label} · {c.formula_used}
                      </div>
                    </div>
                    <div className="text-right text-[10px] text-gray-600">
                      <div>Direction conf: <b className={
                        c.tier === 'ELITE' ? 'text-purple-700' : c.tier === 'STRONG' ? 'text-emerald-700' :
                        c.tier === 'NORMAL' ? 'text-blue-700' : c.tier === 'WIDE' ? 'text-orange-700' : 'text-gray-500'
                      }>{c.effective_confidence_pct}%</b> <span className="text-gray-300 text-[9px]">(raw {c.direction_confidence_pct}%)</span></div>
                      <div>Mode consensus: <b className="font-mono">{c.mode_consensus !== null && c.mode_consensus !== undefined ? (c.mode_consensus > 0 ? `+${c.mode_consensus}` : c.mode_consensus) : '-'}</b></div>
                      <div>Sample: <b>{c.used_matches_total}m</b> · Band <b>±{c.band_pct}%</b></div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-300 rounded p-3 text-sm text-gray-500 italic mb-2">
                  ⚫ No historical precedent for this delta pattern (all scales have {'<'} 3 matches).
                </div>
              )}

              {/* Primary mode counts + fallback matrix if triggered */}
              <div className="flex gap-2 flex-wrap text-[10px] text-gray-500">
                <span>Actual ±{b.actual.tolerance}: <b className="text-gray-700">{b.actual.total_matches}m</b></span>
                <span>Adaptive ±{b.adaptive.pct_tolerance}%: <b className="text-gray-700">{b.adaptive.total_matches}m</b></span>
                {b.fallback_triggered && b.multi_scale_verdict && (
                  <span className={`px-2 rounded ${
                    b.multi_scale_verdict.level === 'SUPER_STRONG' ? 'bg-emerald-100 text-emerald-800' :
                    b.multi_scale_verdict.level === 'STRONG' ? 'bg-emerald-100 text-emerald-700' :
                    b.multi_scale_verdict.level === 'MODERATE' ? 'bg-amber-100 text-amber-800' :
                    b.multi_scale_verdict.level === 'CONFLICTING' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>⚡ {b.multi_scale_verdict.label}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
